import { FC, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { PaginationState } from "@tanstack/react-table"
import { BBColumnDef, Integration } from "../../../../types"
import Card from "../table/card"
import Table from "../../table"
import PreviewModal from "../../preview-modal"
import { useAppDispatch } from "../../../hooks"
import { setPreviewModal } from "../../../../redux/slices/integrations"
import { IntegrationCell } from "../table/cells"
import { ListSkeleton } from "../../skeletons"
import If from "../../if"

interface Props {
  data: Integration[]
  isLoading: boolean
  onSelect?: (id: string) => void
  totalCount?: number
  pagination?: PaginationState
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>
}

const IntegrationTable: FC<Props> = ({
  data,
  isLoading = false,
  onSelect,
  totalCount = 0,
  pagination,
  setPagination
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handleSelectIntegration = (id: string) => {
    if (onSelect) {
      onSelect(id)
    } else {
      navigate(`/integrations/${id}`)
    }
  }

  const pageCount = useMemo(() => {
    if (!pagination || !totalCount) return undefined
    return Math.ceil(totalCount / pagination.pageSize)
  }, [totalCount, pagination])

  const columns: BBColumnDef<Integration>[] = useMemo(
    () => [
      {
        id: "title",
        header: () => <>Name</>,
        footer: props => props.column.id,
        cell: info => (
          <IntegrationCell
            data={info.row.original}
            onSelect={handleSelectIntegration}
          />
        )
      }
    ],
    [handleSelectIntegration]
  )

  return (
    <div className="mt-6">
      <If
        condition={isLoading}
        then={<ListSkeleton />}
        else={
          <div className="px-4">
            <Table
              className="mt-4 -mb-6"
              data={data}
              columns={columns}
              viewType="table"
              pageSize={pagination?.pageSize || 10}
              pageCount={pageCount}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={totalCount}
              searchBarVisible={false}
              renderCard={props => (
                <Card
                  data={props.original}
                  onSelect={handleSelectIntegration}
                />
              )}
              noHeader
              removeMobilePagination
            />
            <PreviewModal
              onClose={() => {
                dispatch(
                  setPreviewModal({
                    isOpen: false,
                    appKey: ""
                  })
                )
              }}
            />
          </div>
        }
      />
    </div>
  )
}
export default IntegrationTable
